import { IDonationConfig } from 'common/neighbors-feeding-neighbors/DonationConfig';
import { GraphQLFormattedError } from 'graphql';
import { ReactNode } from 'react';
import { createGlobalState } from 'src/create-global-state/createGlobalState';
import { CurrentUserFieldsFragment, Maybe, RegionInfo } from 'src/gqlReactTypings.generated.d';
import { initialPromoCodeState } from 'src/promotionals/promotionalsState';
import { initialHelpButtonState } from 'src/shared/design-system/HelpButton/helpButtonState';
import { Routes } from 'src/shared/Routes';
import { initialUserPreferencesState } from 'src/user-preferences/userPreferencesState';

export interface IConfigs {
  appleSignInOnWebEnabled: boolean;
  apiSyncEnabled: boolean;
  chatEnabled: boolean;
  intercomJwtEnabled: boolean;
  chatSupportKeywords: string[];
  chatFeedbackKeywords: string[];
  chatCustomizationKeywords: string[];
  chatBusinessRiskKeywords: string[];
  dishFirstNav: boolean;
  dishwasher: boolean;
  enableAdminPages: boolean;
  giftCardPurchaseEnabled: boolean;
  holidayMenusEnabled: boolean;
  profileDeliveryAddressesEnabled: boolean;
  profilePaymentMethodsEnabled: boolean;
  rateRecentOrderPopupEnabled: boolean;
  requestAppReviewsEnabled: boolean;
  shefDashboardInsights: boolean;
  shefInsightsReportEnabled: boolean;
  shefMenuChecklistEnabled: boolean;
  shefPlatformUsageQuestionnaire: boolean;
  shefPostsEnabled: boolean;
  shefRatingEnabled: boolean;
  shefApplicationAlwaysEnableContinueInformationSessionVideo: boolean;
  consumerDeleteUserEnabled: boolean;
  shefGrowYourBusinessEnabled: boolean;
  shefSideCustomizations: boolean;
  fringeOverrideEnabled: boolean;
  bulkDiscountEnabled: boolean;
  upfrontDeliveryFeeEnabled: boolean;
  mealPlansEnabled: boolean;
  mealPlansAllergensEnabled: boolean;
  mealPlansBrowseAsGuest: boolean;
  showManageMealPlans: boolean;
  upsellsEnabled: boolean;
  reusablePackagingEnabled: boolean;
  shefRosterEnabled: boolean;
  exploreWeeklyShefServiceTabsEnabled: boolean;
  mealPlanDiscountFieldEnabled: boolean;
  forYouPageEnabled: boolean;
  favoritesV2Enabled: boolean;
  mealPlanDeliveryDayStartOverride: Record<number, string>;
  shefCompositeScoreEnabled: boolean;
  foodItemCompositeScoreEnabled: boolean;
  mealPlanAddOnsEnabled: boolean;
  duplicateDishEnabled: boolean;
  nativeShefApplicationQuestionnaire: boolean;
  shefOnboardingRedesign: boolean;
  shefProfileRedesign: boolean;
  newExploreWssNonSubTabEnabled: boolean;
  signUpModalGatingDsEnabled: boolean;
  newReferralValuesEnabled: boolean;
  mealPlanGiftCardsEnabled: boolean;
  mealPlanCheckoutAddOnsEnabled: boolean;
  reusablePackagingCleaningFeeEnabled: boolean;
  merchandiseTagsEnabled: boolean;
  mealPlanCheckoutRevampV1Enabled: boolean;
  foodItemMacrosEnabled: boolean;
  shefPayoutImprovementsEnabled: boolean;
  shefPayoutTiersEnabled: boolean;
  shefPayoutTierSurfacesEnabled: boolean;
  sidesPairingPageEnabled: boolean;
  lunchCategoriesEnabled: boolean;
  mealPlanSearchEnabled: boolean;
  mealPlanSetupIntentEnabled: boolean;
  shefMerchLandingEnabled: boolean;
  variableServiceFeeEnabled: boolean;
  marketplaceVariableServiceFeeEnabled: boolean;
  dynamicShefSortingEnabled: boolean;
  wssOrganicTagsEnabled: boolean;
  shefVarietyToolingEnabled: boolean;
  expressCheckoutV1Enabled: boolean;
  inlineStripeLinkEnabled: boolean;
  ccpaConsentBannerEnabled: boolean;
  shefExtraAvailabilityEnabled: boolean;
  lowCalHighProteinCollectionsEnabled: boolean;
  lowCalHighProteinMacroThresholdEnabled: boolean;
  shefSeasonalToolingEnabled: boolean;
  mealPlanPaymentElementOnAccountEnabled: boolean;
  wssCuisineCarouselEnabledInApp: boolean;
  wssCuisineCarouselV3Enabled: boolean;
  leadCaptureModalEnabled: boolean;
  wssFlexibleServingsV2Enabled: boolean;
  wssFlexibleServingsV3Enabled: boolean;
  flexServingsPaymentEnabled: boolean;
  reusablePackagingBayAreaEnabled: boolean;
  reusablePackagingNyEnabled: boolean;
  reusablePackagingLaEnabled: boolean;
  reusablePackagingSeaEnabled: boolean;
  wssReusableOptInEnabled: boolean;
  wssSteelConsolidationEnabled: boolean;
  lpDishStoriesEnabled: boolean;
  lpFullBleedVideoEnabled: boolean;
  lpHowItWorksEnabled: boolean;
  intakeHouseholdConsolidationV2Enabled: boolean;
  intakeOptimizationEnabled: boolean;
  intakeSettingsBeforeCalendarEnabled: boolean;
  defaultPlanUpdateEnabled: boolean;
  tablerunEnabled: boolean;
  sidesPageSearchEnabled: boolean;
  sidesCategoryCarouselEnabled: boolean;
  signUpGateRedesignEnabled: boolean;
  foodItemValidationEnabled: boolean;
  foodItemValidationPreviewEnabled: boolean;
  foodItemValidationShefFormEnabled: boolean;
  foodItemValidationIssuesPageEnabled: boolean;
  wssDessertProposalsEnabled: boolean;
  hideHomepageOneTimeTxEnabled: boolean;
  mealPlanTippingEnabled: boolean;
  mealPlanRecurringTipEnabled: boolean;
  neighborsFeedingNeighborsEnabled: boolean;
  neighborsFeedingNeighborsMatchActive: boolean;
  neighborsFeedingNeighborsConfig: IDonationConfig | null;
  neighborsFeedingNeighborsMenuLinkEnabled: boolean;
  hubFlowV2ShefPackoutEnabled: boolean;
}

/**
 * The hook which stitches together our various modules for global state. Every module should
 * export its own initial state.
 *
 * The returned `useScopedReducer` hook is the primary way to make new hooks which use global
 * state:
 *
 *    export const useCounter() {
 *      const { state, reduce } = useScopedReducer('counter')
 *      const counter = state.counter;
 *      const increment = reduce(counter => counter + 1);
 *      const decrement = reduce(counter => counter - 1);
 *      return [counter, increment, decrement];
 *    }
 *
 * Note that the API returned by your hook is not restricted to anything in particular.
 */

interface IGlobalFetchState {
  loading: boolean;
  loaded: boolean;
  userRegionId: Maybe<number>;
  userRegionName: Maybe<string>;
  region: Maybe<Pick<RegionInfo, 'id'>>;
  timeZone: Maybe<string>;
  minimumAmountForDiscountedShipping: Maybe<number>;
  discountedDeliveryFee: Maybe<number>;
  deliveryFee: Maybe<number>;
  errors: readonly GraphQLFormattedError[] | string[] | undefined;
  currentUser: Maybe<CurrentUserFieldsFragment>;
  isImpersonating: Maybe<boolean>;
  shefReferral: Maybe<string>;
  configs: IConfigs;
}

const initialGlobalFetchState: IGlobalFetchState = {
  loading: false,
  loaded: false,
  errors: undefined,
  currentUser: null,
  isImpersonating: false,
  userRegionId: null,
  userRegionName: null,
  region: null,
  timeZone: null,
  minimumAmountForDiscountedShipping: null,
  discountedDeliveryFee: null,
  deliveryFee: null,
  shefReferral: null,
  configs: {
    appleSignInOnWebEnabled: false,
    apiSyncEnabled: false,
    chatEnabled: false,
    intercomJwtEnabled: false,
    chatSupportKeywords: [] as string[],
    chatFeedbackKeywords: [] as string[],
    chatCustomizationKeywords: [] as string[],
    chatBusinessRiskKeywords: [] as string[],
    dishFirstNav: false,
    dishwasher: false,
    enableAdminPages: false,
    giftCardPurchaseEnabled: false,
    upsellsEnabled: false,
    holidayMenusEnabled: false,
    profileDeliveryAddressesEnabled: false,
    profilePaymentMethodsEnabled: false,
    rateRecentOrderPopupEnabled: false,
    requestAppReviewsEnabled: false,
    shefDashboardInsights: false,
    shefInsightsReportEnabled: false,
    shefMenuChecklistEnabled: false,
    shefPlatformUsageQuestionnaire: false,
    shefPostsEnabled: false,
    shefRatingEnabled: false,
    shefApplicationAlwaysEnableContinueInformationSessionVideo: true,
    consumerDeleteUserEnabled: false,
    shefGrowYourBusinessEnabled: false,
    shefSideCustomizations: false,
    fringeOverrideEnabled: false,
    bulkDiscountEnabled: false,
    upfrontDeliveryFeeEnabled: false,
    mealPlansEnabled: false,
    mealPlansBrowseAsGuest: false,
    mealPlansAllergensEnabled: false,
    showManageMealPlans: false,
    reusablePackagingEnabled: false,
    shefRosterEnabled: false,
    exploreWeeklyShefServiceTabsEnabled: false,
    mealPlanDiscountFieldEnabled: false,
    forYouPageEnabled: false,
    favoritesV2Enabled: false,
    mealPlanDeliveryDayStartOverride: {},
    mealPlanAddOnsEnabled: false,
    shefCompositeScoreEnabled: false,
    foodItemCompositeScoreEnabled: false,
    duplicateDishEnabled: false,
    nativeShefApplicationQuestionnaire: false,
    shefOnboardingRedesign: false,
    shefProfileRedesign: false,
    newExploreWssNonSubTabEnabled: false,
    signUpModalGatingDsEnabled: false,
    newReferralValuesEnabled: false,
    mealPlanGiftCardsEnabled: false,
    mealPlanCheckoutAddOnsEnabled: false,
    reusablePackagingCleaningFeeEnabled: false,
    merchandiseTagsEnabled: false,
    mealPlanCheckoutRevampV1Enabled: false,
    foodItemMacrosEnabled: false,
    shefPayoutImprovementsEnabled: false,
    shefPayoutTiersEnabled: false,
    shefPayoutTierSurfacesEnabled: false,
    sidesPairingPageEnabled: false,
    lunchCategoriesEnabled: false,
    mealPlanSearchEnabled: false,
    mealPlanSetupIntentEnabled: false,
    shefMerchLandingEnabled: false,
    variableServiceFeeEnabled: false,
    marketplaceVariableServiceFeeEnabled: false,
    dynamicShefSortingEnabled: false,
    wssOrganicTagsEnabled: false,
    shefVarietyToolingEnabled: false,
    expressCheckoutV1Enabled: false,
    inlineStripeLinkEnabled: false,
    ccpaConsentBannerEnabled: false,
    shefExtraAvailabilityEnabled: false,
    lowCalHighProteinCollectionsEnabled: false,
    lowCalHighProteinMacroThresholdEnabled: false,
    shefSeasonalToolingEnabled: false,
    mealPlanPaymentElementOnAccountEnabled: false,
    wssCuisineCarouselEnabledInApp: false,
    wssCuisineCarouselV3Enabled: false,
    leadCaptureModalEnabled: false,
    wssFlexibleServingsV2Enabled: false,
    wssFlexibleServingsV3Enabled: false,
    flexServingsPaymentEnabled: false,
    reusablePackagingBayAreaEnabled: false,
    reusablePackagingNyEnabled: false,
    reusablePackagingLaEnabled: false,
    reusablePackagingSeaEnabled: false,
    wssReusableOptInEnabled: false,
    wssSteelConsolidationEnabled: false,
    lpDishStoriesEnabled: false,
    lpFullBleedVideoEnabled: false,
    lpHowItWorksEnabled: false,
    intakeHouseholdConsolidationV2Enabled: false,
    intakeOptimizationEnabled: false,
    intakeSettingsBeforeCalendarEnabled: false,
    defaultPlanUpdateEnabled: false,
    tablerunEnabled: false,
    sidesPageSearchEnabled: false,
    sidesCategoryCarouselEnabled: false,
    signUpGateRedesignEnabled: false,
    foodItemValidationEnabled: false,
    foodItemValidationPreviewEnabled: false,
    foodItemValidationShefFormEnabled: false,
    foodItemValidationIssuesPageEnabled: false,
    wssDessertProposalsEnabled: false,
    hideHomepageOneTimeTxEnabled: false,
    mealPlanTippingEnabled: false,
    mealPlanRecurringTipEnabled: false,
    neighborsFeedingNeighborsEnabled: false,
    neighborsFeedingNeighborsMatchActive: false,
    neighborsFeedingNeighborsConfig: null,
    neighborsFeedingNeighborsMenuLinkEnabled: false,
    hubFlowV2ShefPackoutEnabled: false,
  },
};

interface IBackButtonState {
  backButtonVisible: boolean;
  backPath: string;
  onBackButtonClick: (() => void) | undefined;
}

const initialBackButtonState: IBackButtonState = {
  backButtonVisible: false,
  backPath: Routes.CONSUMER_LANDING,
  onBackButtonClick: undefined,
};

/**
 * Opt-in slot for the consumer header's right-hand side. A page sets a node while it is
 * mounted and clears it on unmount. While it is undefined the header renders whatever it
 * would otherwise show, so pages that never set it are unaffected.
 */
interface IHeaderRightActionState {
  headerRightAction: ReactNode;
}

const initialHeaderRightActionState: IHeaderRightActionState = {
  headerRightAction: undefined,
};

export const initialState = {
  ...initialUserPreferencesState,
  ...initialPromoCodeState,
  ...initialGlobalFetchState,
  ...initialHelpButtonState,
  ...initialBackButtonState,
  ...initialHeaderRightActionState,
};

export const { useGlobalReducer, useScopedReducer, GlobalStateProvider } = createGlobalState(initialState);
